// TopChrome.tsx — App header. V29 S1: the hamburger is gone (navigation lives
// in the bottom TabBar). Two variants, chosen by the screen that renders it:
//   - DEEP screens pass `onBack` → a 44px back arrow leads the row;
//   - TOP-LEVEL screens omit `onBack` → no leading button, and (unless
//     `profileChip={false}`) a gradient avatar chip with the student's real
//     initial sits at the far right → /profilo. The chip reads the profile
//     hydrated from /api/me (server truth, F21) — never a hardcoded letter.
// The unread badge moved to the TabBar's Messaggi tab.
// Chrome stays SCREEN-OWNED (plan v1.2 F8a disposition): screens with live
// chrome state (the Simula timer ticking 1/sec) re-render only their own tree.

import type { JSX, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTokens } from '../state/theme';
import { MODE_ICONS, type ModeKey } from './modeIcons';
import { getStoredProfile } from '../state/profile';
import { BRAND_GRADIENT } from '../tokens';

interface TopChromeProps {
  title: string;
  subtitle?: string;
  mode?: ModeKey;
  /** Optional leading node (e.g. the Il Prof avatar) shown in place of the mode icon. */
  leading?: ReactNode;
  actions?: ReactNode;
  timer?: string;
  /** Urgency level for the timer pill: tints the color as time runs low. */
  timerState?: 'normal' | 'advisory' | 'urgent';
  /** Deep screens: back arrow handler (usually useShellNav().goBack). */
  onBack?: () => void;
  /** Top-level screens show the profile chip by default; pass false to hide
   *  (e.g. the in-exam chrome, which must stay free of app chrome). */
  profileChip?: boolean;
  onNew?: () => void;
}

export function TopChrome({
  title,
  subtitle,
  mode,
  leading,
  actions,
  timer,
  timerState = 'normal',
  onBack,
  profileChip = true,
  onNew,
}: TopChromeProps): JSX.Element {
  const tokens = useTokens();
  const navigate = useNavigate();
  // Redesign §3.7 / V4: frosted translucent chrome so the atmosphere washes glow
  // through at the corners. Mode-aware tint over a backdrop blur.
  const chromeBg = tokens.mode === 'dark' ? 'rgba(22,16,31,0.55)' : 'rgba(255,255,255,0.55)';
  const timerBorder =
    timerState === 'urgent' ? tokens.wrong :
    timerState === 'advisory' ? tokens.accent :
    tokens.line;
  const timerColor =
    timerState === 'urgent' ? tokens.wrong :
    timerState === 'advisory' ? tokens.accent :
    tokens.text;
  const timerBackground =
    timerState === 'urgent' ? tokens.wrongSoft :
    timerState === 'advisory' ? tokens.accentSoft :
    tokens.surface;

  // Profile chip initial — the REAL first name from the /api/me-hydrated
  // profile; a neutral person glyph when no name is known yet.
  const showChip = !onBack && profileChip;
  const firstName = showChip ? getStoredProfile()?.firstName?.trim() : undefined;
  const initial = firstName ? firstName[0]!.toUpperCase() : null;

  return (
    <div
      style={{
        display:      'flex',
        alignItems:   'center',
        gap:          8,
        padding:      'calc(var(--sai-top) + 14px) 12px 12px',
        borderBottom: `1px solid ${tokens.line}`,
        background:   chromeBg,
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        flexShrink:   0,
      }}
    >
      {/* Back arrow — deep screens only. 44x44 (WCAG 2.5.5). */}
      {onBack && (
        <button
          onClick={onBack}
          data-testid="chrome-back"
          style={{
            width:           44,
            height:          44,
            borderRadius:    10,
            background:      'transparent',
            border:          'none',
            cursor:          'pointer',
            display:         'flex',
            alignItems:      'center',
            justifyContent:  'center',
            color:           tokens.text,
            marginLeft:      -6,
            flexShrink:      0,
          }}
          aria-label="Indietro"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
      )}

      {/* Title + subtitle block */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
        {leading ?? (mode && MODE_ICONS[mode] && MODE_ICONS[mode](tokens.accent))}
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontFamily:    tokens.fonts.body,
              fontSize:      14,
              fontWeight:    600,
              color:         tokens.text,
              letterSpacing: -0.1,
              whiteSpace:    'nowrap',
              overflow:      'hidden',
              textOverflow:  'ellipsis',
            }}
          >
            {title}
          </div>
          {subtitle && (
            <div
              style={{
                fontFamily:     tokens.fonts.mono,
                fontSize:       10,
                color:          tokens.textFaint,
                letterSpacing:  0.4,
                textTransform:  'uppercase',
                marginTop:      1,
                whiteSpace:     'nowrap',
                overflow:       'hidden',
                textOverflow:   'ellipsis',
              }}
            >
              {subtitle}
            </div>
          )}
        </div>
      </div>

      {/* Optional per-screen actions */}
      {actions && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexShrink: 0,
          }}
        >
          {actions}
        </div>
      )}

      {timer && (
        <div
          style={{
            fontFamily:         tokens.fonts.mono,
            fontSize:           14,
            fontWeight:         timerState === 'urgent' ? 700 : 500,
            color:              timerColor,
            fontVariantNumeric: 'tabular-nums',
            padding:            '4px 10px',
            background:         timerBackground,
            border:             `1px solid ${timerBorder}`,
            borderRadius:       8,
            // Spec §5.5: the exam timer shifts color by urgency but NEVER blinks
            // (a flashing element under time pressure is an anxiety / motion-
            // sensitivity trigger). Urgency is announced via aria-live instead.
          }}
          aria-live={timerState === 'urgent' ? 'assertive' : undefined}
          aria-label={timer}
        >
          {timer}
        </div>
      )}

      {/* Optional "nuova conversazione" button — shown when onNew is provided */}
      {onNew && (
        <button
          onClick={onNew}
          style={{
            width:          44,
            height:         44,
            borderRadius:   10,
            background:     'transparent',
            border:         'none',
            cursor:         'pointer',
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'center',
            color:          tokens.textMuted,
          }}
          aria-label="Nuova conversazione"
          title="Nuova conversazione"
        >
          {/* Pen-on-square "compose new" icon — matches the "new message" pattern in
              familiar chat apps (Messages, Slack). */}
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h7M4 4v12h12v-7"/>
            <path d="M13 3l4 4-8 8H5v-4l8-8z"/>
          </svg>
        </button>
      )}

      {/* Profile chip — top-level screens. Whole 44px hit area; the visible
          circle is 34px with the student's real initial on the brand gradient. */}
      {showChip && (
        <button
          onClick={() => navigate('/profilo')}
          data-testid="chrome-profile-chip"
          aria-label="Apri profilo e impostazioni"
          style={{
            width:          44,
            height:         44,
            border:         'none',
            background:     'transparent',
            cursor:         'pointer',
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'center',
            marginRight:    -6,
            flexShrink:     0,
            padding:        0,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width:          34,
              height:         34,
              borderRadius:   999,
              background:     BRAND_GRADIENT[tokens.mode],
              color:          '#fff',
              display:        'inline-flex',
              alignItems:     'center',
              justifyContent: 'center',
              fontFamily:     tokens.fonts.body,
              fontSize:       14,
              fontWeight:     700,
              border:         `2px solid ${tokens.surface}`,
              boxShadow:      '0 3px 10px rgba(105, 67, 255, 0.28)',
            }}
          >
            {initial ?? (
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="10" cy="7" r="3" />
                <path d="M4 17c1.5-3 4-4 6-4s4.5 1 6 4" />
              </svg>
            )}
          </span>
        </button>
      )}
    </div>
  );
}

export function TopChromeActionButton({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick: () => void;
}): JSX.Element {
  const tokens = useTokens();
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        minHeight: 34,
        padding: '0 12px',
        borderRadius: tokens.radius.pill,
        border: `1px solid ${tokens.lineStrong}`,
        background: tokens.surface,
        color: tokens.accentInk,
        cursor: 'pointer',
        fontFamily: tokens.fonts.body,
        fontSize: 12.5,
        fontWeight: 700,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}
